import { useState, useEffect } from 'react';
import axios from 'axios';
import { Package, Plus, Trash2, Edit2, AlertTriangle, X, Save, Archive, ArrowRight, RefreshCw, Store, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const PRODUCT_TYPES = ['Poster', 'Canvas', 'Acrylic Print', 'Shirt', 'Frame', 'Trading Card', 'Other'];
const CANVAS_SIZES = ['24x18', '32x24', '40x30', '48x36'];
const SHIRT_SIZES = ['XS', 'S', 'M', 'L', 'XL', '2XL', '3XL', '4XL', '5XL'];

export default function Inventory() {
  const [inventory, setInventory] = useState([]);
  const [lowStock, setLowStock] = useState([]);
  const [events, setEvents] = useState([]);
  const [eventInventory, setEventInventory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('wix');
  const [selectedEvent, setSelectedEvent] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [showEventForm, setShowEventForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  
  // Wix products state
  const [selectedStore, setSelectedStore] = useState('lsd_collections');
  const [wixProducts, setWixProducts] = useState({ lsd_collections: [], vyrus_clothing: [] });
  const [wixInventory, setWixInventory] = useState({});
  const [loadingWix, setLoadingWix] = useState(false);
  const [editingWixProduct, setEditingWixProduct] = useState(null);
  const [wixProductEdits, setWixProductEdits] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [savingInventory, setSavingInventory] = useState({});
  
  // Form state for manual inventory
  const [formData, setFormData] = useState({
    product_type: '',
    product_name: '',
    size: '',
    total_stock: 0,
    reorder_threshold: 10,
    unit_cost: '',
    notes: ''
  });

  // Event inventory form
  const [eventFormData, setEventFormData] = useState({
    event_id: '',
    inventory_id: '',
    starting_count: 0,
    notes: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (selectedEvent) {
      fetchEventInventory(selectedEvent);
    }
  }, [selectedEvent]);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('vyrus_token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const [invRes, lowStockRes, eventsRes, wixInvRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/inventory`, { headers }),
        axios.get(`${BACKEND_URL}/api/inventory/low-stock`, { headers }),
        axios.get(`${BACKEND_URL}/api/events`, { headers }),
        axios.get(`${BACKEND_URL}/api/wix-inventory`, { headers })
      ]);
      
      setInventory(invRes.data);
      setLowStock(lowStockRes.data);
      setEvents(eventsRes.data);
      setWixInventory(wixInvRes.data || {});
      
      // Fetch Wix products for both stores
      fetchWixProducts('lsd_collections');
      fetchWixProducts('vyrus_clothing');
    } catch (error) {
      toast.error('Failed to load inventory');
    } finally {
      setLoading(false);
    }
  };

  const fetchWixProducts = async (store) => {
    setLoadingWix(true);
    try {
      const token = localStorage.getItem('vyrus_token');
      const response = await axios.get(`${BACKEND_URL}/api/wix/products/${store}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWixProducts(prev => ({
        ...prev,
        [store]: response.data.products || []
      }));
    } catch (error) {
      console.error(`Failed to fetch Wix products for ${store}:`, error);
      setWixProducts(prev => ({
        ...prev,
        [store]: []
      }));
    } finally {
      setLoadingWix(false);
    }
  };

  const fetchEventInventory = async (eventId) => {
    try {
      const token = localStorage.getItem('vyrus_token');
      const response = await axios.get(`${BACKEND_URL}/api/event-inventory/${eventId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEventInventory(response.data);
    } catch (error) {
      console.error('Failed to fetch event inventory');
    }
  };

  // Update Wix product inventory count - saves to backend
  const updateWixInventoryCount = async (productId, count) => {
    setSavingInventory(prev => ({ ...prev, [productId]: true }));
    try {
      const token = localStorage.getItem('vyrus_token');
      const localData = wixInventory[productId] || {};
      
      await axios.put(`${BACKEND_URL}/api/wix-inventory/${productId}`, {
        product_id: productId,
        store: selectedStore,
        count: parseInt(count) || 0,
        custom_name: localData.custom_name || null,
        custom_sku: localData.custom_sku || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setWixInventory(prev => ({
        ...prev,
        [productId]: {
          ...prev[productId],
          count: parseInt(count) || 0
        }
      }));
      
      toast.success('Inventory count saved');
    } catch (error) {
      toast.error('Failed to save inventory count');
    } finally {
      setSavingInventory(prev => ({ ...prev, [productId]: false }));
    }
  };

  const saveWixProductEdits = async (productId) => {
    const edits = wixProductEdits[productId];
    const localData = wixInventory[productId] || {};
    
    setSavingInventory(prev => ({ ...prev, [productId]: true }));
    try {
      const token = localStorage.getItem('vyrus_token');
      
      await axios.put(`${BACKEND_URL}/api/wix-inventory/${productId}`, {
        product_id: productId,
        store: selectedStore,
        count: edits?.count ?? localData.count ?? 0,
        custom_name: edits?.custom_name ?? localData.custom_name ?? null,
        custom_sku: edits?.custom_sku ?? localData.custom_sku ?? null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setWixInventory(prev => ({
        ...prev,
        [productId]: {
          ...prev[productId],
          ...edits
        }
      }));
      
      setEditingWixProduct(null);
      setWixProductEdits(prev => {
        const { [productId]: _, ...rest } = prev;
        return rest;
      });
      toast.success('Product details saved');
    } catch (error) {
      toast.error('Failed to save product details');
    } finally {
      setSavingInventory(prev => ({ ...prev, [productId]: false }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.product_type) {
      toast.error('Please select product type');
      return;
    }

    const payload = {
      ...formData,
      total_stock: parseInt(formData.total_stock) || 0,
      reorder_threshold: parseInt(formData.reorder_threshold) || 10,
      unit_cost: formData.unit_cost ? parseFloat(formData.unit_cost) : null
    };

    try {
      const token = localStorage.getItem('vyrus_token');
      if (editingItem) {
        await axios.put(`${BACKEND_URL}/api/inventory/${editingItem.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Item updated');
      } else {
        await axios.post(`${BACKEND_URL}/api/inventory`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Item added');
      }
      
      setShowForm(false);
      setEditingItem(null);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error('Failed to save');
    }
  };

  const handleEventInventorySubmit = async (e) => {
    e.preventDefault();
    
    if (!eventFormData.event_id || !eventFormData.inventory_id) {
      toast.error('Please select event and item');
      return;
    }

    try {
      const token = localStorage.getItem('vyrus_token');
      await axios.post(`${BACKEND_URL}/api/event-inventory`, {
        ...eventFormData,
        starting_count: parseInt(eventFormData.starting_count) || 0
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Event inventory added');
      setShowEventForm(false);
      setEventFormData({ event_id: selectedEvent, inventory_id: '', starting_count: 0, notes: '' });
      fetchEventInventory(selectedEvent);
    } catch (error) {
      toast.error('Failed to save');
    }
  };

  const updateEndingCount = async (recordId, endingCount) => {
    try {
      const token = localStorage.getItem('vyrus_token');
      await axios.put(`${BACKEND_URL}/api/event-inventory/${recordId}?ending_count=${endingCount}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Ending count updated & inventory adjusted');
      fetchEventInventory(selectedEvent);
      fetchData();
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  const handleDelete = async (itemId) => {
    if (!window.confirm('Delete this inventory item?')) return;
    
    try {
      const token = localStorage.getItem('vyrus_token');
      await axios.delete(`${BACKEND_URL}/api/inventory/${itemId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      product_type: item.product_type,
      product_name: item.product_name || '',
      size: item.size || '',
      total_stock: item.total_stock,
      reorder_threshold: item.reorder_threshold,
      unit_cost: item.unit_cost || '',
      notes: item.notes || ''
    });
    setShowForm(true);
  };

  const resetForm = () => {
    setFormData({
      product_type: '',
      product_name: '',
      size: '',
      total_stock: 0,
      reorder_threshold: 10,
      unit_cost: '',
      notes: ''
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };

  const getItemDisplayName = (item) => {
    let name = item.product_type;
    if (item.product_name) name += ` - ${item.product_name}`;
    if (item.size) name += ` (${item.size})`;
    return name;
  };

  // Target Wix Collection IDs for LSD inventory
  const TARGET_COLLECTIONS = {
    lsd_collections: [
      '6bf7c880-a62b-9f30-1cf9-eda1bd710a86',  // Coloring Book
      '00648517-3b8a-5d4a-44e2-c206ef951cdd',  // Color With Vyrus
      '58047674-cc82-e376-e80e-ce1211940932',  // Entire Canvas Collections
      '531117c3-ee74-e804-92d5-bf42f860d0f6',  // LSD Canvases  
      '0318a0df-7972-c00d-f2db-30f91ede7312',  // Vyrus Canvases
      'b1e441bb-3e82-accc-2f87-58ffa88800c8',  // Entire Trading Card Products
      '6170acc9-4fb0-818b-38cf-0921809add43',  // Vyrus Trading Card Collection
      '6cb5ff72-f363-01b6-2ec9-f7b1b8b88260',  // Collaboration Trading Cards
      '3c4f22f9-c544-b307-d49d-d46d35b4fe11',  // Entire V Glass Acrylic Collection
      '1924461d-d510-cf02-6e3a-a9894956409a',  // V Glass (LSD) Acrylics
      'b40f0536-5fad-247a-4a97-f132fedaf143',  // V Glass (VYRUS) Acrylics
      '6beb4f5c-f99a-52b6-87d3-a53510d0a661',  // Entire Poster Print Collection
      '5c661e41-8ac3-9525-cd16-585197f9dd47',  // LSD Poster Prints
      '9823a990-3dbc-c616-b375-b2eb78a95b46',  // Vyrus Poster Prints
    ]
  };

  // Filter Wix products by collection IDs
  // Exclude (WS) products
  const filterWixProducts = (products, store) => {
    return products.filter(p => {
      const name = p.name.toLowerCase();
      
      // Exclude (WS) products
      if (name.includes('(ws)')) return false;
      
      // For LSD Collections - filter by collection membership
      if (store === 'lsd_collections') {
        const productCollections = p.collectionIds || [];
        const targetCollections = TARGET_COLLECTIONS.lsd_collections || [];
        
        // Check if product belongs to any target collection
        return productCollections.some(cid => targetCollections.includes(cid));
      }
      
      // For Vyrus Clothing - these are all shirts, include all
      if (store === 'vyrus_clothing') {
        return true;
      }
      
      return false;
    });
  };

  // Filter Wix products based on search (after type filtering)
  const filterProductsBySearch = (products) => {
    if (!searchTerm) return products;
    const term = searchTerm.toLowerCase();
    return products.filter(p => {
      const localData = wixInventory[p.id] || {};
      return (
        p.name.toLowerCase().includes(term) ||
        (p.sku && p.sku.toLowerCase().includes(term)) ||
        (localData.custom_name && localData.custom_name.toLowerCase().includes(term)) ||
        (localData.custom_sku && localData.custom_sku.toLowerCase().includes(term))
      );
    });
  };

  // Apply both filters: type filter first, then search filter
  const getFilteredProducts = () => {
    const typeFiltered = filterWixProducts(wixProducts[selectedStore] || [], selectedStore);
    return filterProductsBySearch(typeFiltered);
  };

  const currentProducts = getFilteredProducts();

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="text-muted-foreground">Loading...</div></div>;
  }

  return (
    <div className="space-y-4 sm:space-y-6 pb-20" data-testid="inventory-page">
      <div>
        <h1 className="text-2xl sm:text-4xl font-bold font-heading tracking-tight">Inventory</h1>
        <p className="text-muted-foreground mt-1 text-sm">Canvases, Posters, Trading Cards, Acrylic Prints, Shirts & Coloring Books</p>
      </div>

      {/* Low Stock Alert */}
      {lowStock.length > 0 && (
        <Card className="bg-red-500/10 border-red-500/30">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-red-500">
              <AlertTriangle className="h-5 w-5" />
              <span className="font-semibold">{lowStock.length} item(s) low on stock!</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="w-full grid grid-cols-4 h-auto">
          <TabsTrigger value="wix" className="text-xs sm:text-sm py-2">
            <Store className="h-4 w-4 mr-1 hidden sm:inline" /> Wix Products
          </TabsTrigger>
          <TabsTrigger value="manual" className="text-xs sm:text-sm py-2">
            <Package className="h-4 w-4 mr-1 hidden sm:inline" /> Manual
          </TabsTrigger>
          <TabsTrigger value="event" className="text-xs sm:text-sm py-2">
            <Archive className="h-4 w-4 mr-1 hidden sm:inline" /> Event
          </TabsTrigger>
          <TabsTrigger value="alerts" className="text-xs sm:text-sm py-2 relative">
            <AlertTriangle className="h-4 w-4 mr-1 hidden sm:inline" /> Low Stock
            {lowStock.length > 0 && (
              <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-4 w-4 flex items-center justify-center text-[10px]">
                {lowStock.length}
              </span>
            )}
          </TabsTrigger>
        </TabsList>

        {/* Wix Products Tab */}
        <TabsContent value="wix" className="mt-4 space-y-4">
          {/* Store Selection & Search */}
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex gap-2">
              <Button
                variant={selectedStore === 'lsd_collections' ? 'default' : 'outline'}
                onClick={() => setSelectedStore('lsd_collections')}
                size="sm"
              >
                LSD ({filterWixProducts(wixProducts.lsd_collections || [], 'lsd_collections').length})
              </Button>
              <Button
                variant={selectedStore === 'vyrus_clothing' ? 'default' : 'outline'}
                onClick={() => setSelectedStore('vyrus_clothing')}
                size="sm"
              >
                Vyrus Shirts ({filterWixProducts(wixProducts.vyrus_clothing || [], 'vyrus_clothing').length})
              </Button>
            </div>
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search products..."
                className="pl-9 bg-secondary/50"
              />
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => fetchWixProducts(selectedStore)}
              disabled={loadingWix}
            >
              <RefreshCw className={`h-4 w-4 mr-1 ${loadingWix ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>

          {/* Products Grid */}
          {loadingWix ? (
            <Card className="p-8 text-center bg-card border-border/50">
              <RefreshCw className="h-8 w-8 text-muted-foreground mx-auto mb-4 animate-spin" />
              <p className="text-muted-foreground">Loading products from Wix...</p>
            </Card>
          ) : currentProducts.length === 0 ? (
            <Card className="p-8 text-center bg-card border-border/50">
              <Store className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">
                {searchTerm ? 'No products match your search' : 'No products found in this store'}
              </p>
            </Card>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                Showing {currentProducts.length} products • Click a count to edit
              </p>
              {currentProducts.map((product) => {
                const localData = wixInventory[product.id] || {};
                const displayName = localData.custom_name || product.name;
                const displaySku = localData.custom_sku || product.sku || '';
                const stockCount = localData.count || 0;
                const isEditing = editingWixProduct === product.id;
                const isSaving = savingInventory[product.id];
                
                return (
                  <Card key={product.id} className="bg-card border-border/50" data-testid={`wix-product-${product.id}`}>
                    <CardContent className="p-4">
                      <div className="flex flex-col sm:flex-row gap-4">
                        {/* Product Image */}
                        {product.media && (
                          <div className="w-16 h-16 flex-shrink-0">
                            <img 
                              src={product.media} 
                              alt={displayName}
                              className="w-full h-full object-cover rounded-lg"
                            />
                          </div>
                        )}
                        
                        {/* Product Info */}
                        <div className="flex-1 min-w-0">
                          {isEditing ? (
                            <div className="space-y-2">
                              <Input
                                value={wixProductEdits[product.id]?.custom_name ?? localData.custom_name ?? product.name}
                                onChange={(e) => setWixProductEdits(prev => ({
                                  ...prev,
                                  [product.id]: { ...prev[product.id], custom_name: e.target.value }
                                }))}
                                placeholder="Custom product name"
                                className="bg-secondary/50 text-sm"
                              />
                              <Input
                                value={wixProductEdits[product.id]?.custom_sku ?? localData.custom_sku ?? product.sku ?? ''}
                                onChange={(e) => setWixProductEdits(prev => ({
                                  ...prev,
                                  [product.id]: { ...prev[product.id], custom_sku: e.target.value }
                                }))}
                                placeholder="Custom SKU"
                                className="bg-secondary/50 text-sm"
                              />
                              <div className="flex gap-2">
                                <Button size="sm" onClick={() => saveWixProductEdits(product.id)} disabled={isSaving}>
                                  <Save className="h-3 w-3 mr-1" /> {isSaving ? 'Saving...' : 'Save'}
                                </Button>
                                <Button size="sm" variant="outline" onClick={() => {
                                  setEditingWixProduct(null);
                                  setWixProductEdits(prev => {
                                    const { [product.id]: _, ...rest } = prev;
                                    return rest;
                                  });
                                }}>
                                  Cancel
                                </Button>
                              </div>
                            </div>
                          ) : (
                            <>
                              <div className="flex items-start justify-between gap-2">
                                <div className="min-w-0">
                                  <p className="font-semibold text-sm truncate">{displayName}</p>
                                  {displaySku && (
                                    <p className="text-xs text-muted-foreground">SKU: {displaySku}</p>
                                  )}
                                  {localData.custom_name && (
                                    <p className="text-xs text-muted-foreground/60">Original: {product.name}</p>
                                  )}
                                </div>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => setEditingWixProduct(product.id)}
                                  className="flex-shrink-0"
                                >
                                  <Edit2 className="h-3 w-3" />
                                </Button>
                              </div>
                            </>
                          )}
                        </div>
                        
                        {/* Stock Count */}
                        <div className="flex items-center gap-3 flex-shrink-0">
                          <div className="text-center">
                            <p className="text-xs text-muted-foreground mb-1">In Stock</p>
                            <div className="flex items-center gap-1">
                              <Input
                                type="number"
                                min="0"
                                value={stockCount}
                                onChange={(e) => {
                                  setWixInventory(prev => ({
                                    ...prev,
                                    [product.id]: {
                                      ...prev[product.id],
                                      count: parseInt(e.target.value) || 0
                                    }
                                  }));
                                }}
                                onBlur={(e) => updateWixInventoryCount(product.id, e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    e.target.blur();
                                  }
                                }}
                                className="w-20 h-8 text-center bg-secondary/50 font-mono text-lg"
                                disabled={isSaving}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* Manual Inventory Tab */}
        <TabsContent value="manual" className="mt-4 space-y-4">
          <div className="flex justify-end">
            <Button onClick={() => { resetForm(); setShowForm(true); setEditingItem(null); }}>
              <Plus className="h-4 w-4 mr-2" /> Add Item
            </Button>
          </div>

          <div className="space-y-3">
            {inventory.length === 0 ? (
              <Card className="p-8 text-center bg-card border-border/50">
                <Package className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">No manual inventory items yet</p>
              </Card>
            ) : (
              inventory.map((item) => (
                <Card key={item.id} className={`bg-card border-border/50 ${item.total_stock <= item.reorder_threshold ? 'border-red-500/50' : ''}`}>
                  <CardContent className="p-4">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold">{getItemDisplayName(item)}</span>
                          {item.total_stock <= item.reorder_threshold && (
                            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-500/20 text-red-500">
                              Low Stock
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground">
                          Reorder at: {item.reorder_threshold} units
                          {item.unit_cost && ` • Cost: ${formatCurrency(item.unit_cost)}`}
                        </p>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-right">
                          <p className={`text-2xl font-bold font-mono ${item.total_stock <= item.reorder_threshold ? 'text-red-500' : 'text-primary'}`}>
                            {item.total_stock}
                          </p>
                          <p className="text-xs text-muted-foreground">in stock</p>
                        </div>
                        <Button variant="ghost" size="sm" onClick={() => handleEdit(item)}>
                          <Edit2 className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="sm" className="text-destructive" onClick={() => handleDelete(item.id)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </TabsContent>

        {/* Event Tracking Tab */}
        <TabsContent value="event" className="mt-4 space-y-4">
          <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
            <select
              value={selectedEvent}
              onChange={(e) => setSelectedEvent(e.target.value)}
              className="px-4 py-2 rounded-lg bg-secondary/50 border border-transparent focus:border-primary text-foreground"
            >
              <option value="">Select Event</option>
              {events.map(event => (
                <option key={event.id} value={event.id}>{event.name}</option>
              ))}
            </select>
            
            {selectedEvent && (
              <Button onClick={() => { setEventFormData({...eventFormData, event_id: selectedEvent}); setShowEventForm(true); }} className="ml-auto">
                <Plus className="h-4 w-4 mr-2" /> Add to Event
              </Button>
            )}
          </div>

          {!selectedEvent ? (
            <Card className="p-8 text-center bg-card border-border/50">
              <Archive className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">Select an event to track inventory</p>
            </Card>
          ) : eventInventory.length === 0 ? (
            <Card className="p-8 text-center bg-card border-border/50">
              <Package className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No inventory tracked for this event</p>
            </Card>
          ) : (
            <div className="space-y-3">
              {eventInventory.map((record) => {
                const item = record.inventory_item;
                const sold = record.ending_count !== null ? record.starting_count - record.ending_count : null;
                
                return (
                  <Card key={record.id} className="bg-card border-border/50">
                    <CardContent className="p-4">
                      <div className="flex flex-col gap-3">
                        <div className="flex items-center justify-between">
                          <span className="font-semibold">{item ? getItemDisplayName(item) : 'Unknown Item'}</span>
                          {sold !== null && sold > 0 && (
                            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-500/20 text-green-500">
                              {sold} sold
                            </span>
                          )}
                        </div>
                        
                        <div className="flex items-center gap-4 text-sm">
                          <div className="text-center">
                            <p className="text-muted-foreground text-xs">Start</p>
                            <p className="font-bold text-lg">{record.starting_count}</p>
                          </div>
                          <ArrowRight className="h-4 w-4 text-muted-foreground" />
                          <div className="text-center flex-1">
                            {record.ending_count !== null ? (
                              <>
                                <p className="text-muted-foreground text-xs">End</p>
                                <p className="font-bold text-lg">{record.ending_count}</p>
                              </>
                            ) : (
                              <div className="flex items-center gap-2">
                                <Input
                                  type="number"
                                  min="0"
                                  max={record.starting_count}
                                  placeholder="Ending count"
                                  className="w-24 h-8 text-center bg-secondary/50"
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                      updateEndingCount(record.id, parseInt(e.target.value));
                                    }
                                  }}
                                />
                                <Button size="sm" variant="outline" onClick={(e) => {
                                  const input = e.target.previousSibling;
                                  if (input?.value) {
                                    updateEndingCount(record.id, parseInt(input.value));
                                  }
                                }}>
                                  Save
                                </Button>
                              </div>
                            )}
                          </div>
                          {sold !== null && (
                            <>
                              <ArrowRight className="h-4 w-4 text-muted-foreground" />
                              <div className="text-center">
                                <p className="text-muted-foreground text-xs">Sold</p>
                                <p className="font-bold text-lg text-green-500">{sold}</p>
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* Low Stock Alerts Tab */}
        <TabsContent value="alerts" className="mt-4 space-y-3">
          {lowStock.length === 0 ? (
            <Card className="p-8 text-center bg-card border-border/50">
              <Package className="h-12 w-12 text-green-500 mx-auto mb-4" />
              <p className="text-green-500 font-medium">All items are well-stocked!</p>
            </Card>
          ) : (
            lowStock.map((item) => (
              <Card key={item.id} className="bg-card border-red-500/50">
                <CardContent className="p-4">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <AlertTriangle className="h-5 w-5 text-red-500" />
                        <span className="font-semibold">{getItemDisplayName(item)}</span>
                      </div>
                      <p className="text-sm text-muted-foreground mt-1">
                        {item.total_stock} remaining (reorder at {item.reorder_threshold})
                      </p>
                    </div>
                    <Button variant="outline" size="sm" onClick={() => handleEdit(item)}>
                      Update Stock
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </TabsContent>
      </Tabs>

      {/* Add/Edit Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md max-h-[90vh] overflow-y-auto bg-card">
            <CardHeader className="border-b border-border flex-row items-center justify-between">
              <CardTitle>{editingItem ? 'Edit Item' : 'Add Inventory Item'}</CardTitle>
              <Button variant="ghost" size="sm" onClick={() => { setShowForm(false); setEditingItem(null); }}>
                <X className="h-4 w-4" />
              </Button>
            </CardHeader>
            <CardContent className="p-4">
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="text-sm text-muted-foreground mb-1 block">Product Type *</label>
                  <select
                    value={formData.product_type}
                    onChange={(e) => setFormData({...formData, product_type: e.target.value, size: ''})}
                    className="w-full px-3 py-2 rounded-lg bg-secondary/50 border border-transparent focus:border-primary text-foreground"
                    required
                  >
                    <option value="">Select Type</option>
                    {PRODUCT_TYPES.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="text-sm text-muted-foreground mb-1 block">Product Name/Design</label>
                  <Input
                    value={formData.product_name}
                    onChange={(e) => setFormData({...formData, product_name: e.target.value})}
                    placeholder="e.g., Toga Waifu, Gear 5 Luffy"
                    className="bg-secondary/50 border-transparent focus:border-primary"
                  />
                </div>

                {(formData.product_type === 'Canvas' || formData.product_type === 'Shirt') && (
                  <div>
                    <label className="text-sm text-muted-foreground mb-1 block">Size</label>
                    <select
                      value={formData.size}
                      onChange={(e) => setFormData({...formData, size: e.target.value})}
                      className="w-full px-3 py-2 rounded-lg bg-secondary/50 border border-transparent focus:border-primary text-foreground"
                    >
                      <option value="">Select Size</option>
                      {(formData.product_type === 'Canvas' ? CANVAS_SIZES : SHIRT_SIZES).map(size => (
                        <option key={size} value={size}>{size}</option>
                      ))}
                    </select>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-sm text-muted-foreground mb-1 block">Current Stock *</label>
                    <Input
                      type="number"
                      min="0"
                      value={formData.total_stock}
                      onChange={(e) => setFormData({...formData, total_stock: e.target.value})}
                      className="bg-secondary/50 border-transparent focus:border-primary"
                      required
                    />
                  </div>
                  <div>
                    <label className="text-sm text-muted-foreground mb-1 block">Reorder At</label>
                    <Input
                      type="number"
                      min="0"
                      value={formData.reorder_threshold}
                      onChange={(e) => setFormData({...formData, reorder_threshold: e.target.value})}
                      className="bg-secondary/50 border-transparent focus:border-primary"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-sm text-muted-foreground mb-1 block">Unit Cost (Optional)</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.unit_cost}
                      onChange={(e) => setFormData({...formData, unit_cost: e.target.value})}
                      className="pl-7 bg-secondary/50 border-transparent focus:border-primary"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-sm text-muted-foreground mb-1 block">Notes</label>
                  <Input
                    value={formData.notes}
                    onChange={(e) => setFormData({...formData, notes: e.target.value})}
                    placeholder="Optional notes"
                    className="bg-secondary/50 border-transparent focus:border-primary"
                  />
                </div>

                <Button type="submit" className="w-full">
                  <Save className="h-4 w-4 mr-2" /> {editingItem ? 'Update' : 'Add'} Item
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Event Inventory Form Modal */}
      {showEventForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md bg-card">
            <CardHeader className="border-b border-border flex-row items-center justify-between">
              <CardTitle>Add Item to Event</CardTitle>
              <Button variant="ghost" size="sm" onClick={() => setShowEventForm(false)}>
                <X className="h-4 w-4" />
              </Button>
            </CardHeader>
            <CardContent className="p-4">
              <form onSubmit={handleEventInventorySubmit} className="space-y-4">
                <div>
                  <label className="text-sm text-muted-foreground mb-1 block">Inventory Item *</label>
                  <select
                    value={eventFormData.inventory_id}
                    onChange={(e) => setEventFormData({...eventFormData, inventory_id: e.target.value})}
                    className="w-full px-3 py-2 rounded-lg bg-secondary/50 border border-transparent focus:border-primary text-foreground"
                    required
                  >
                    <option value="">Select Item</option>
                    {inventory.map(item => (
                      <option key={item.id} value={item.id}>{getItemDisplayName(item)} ({item.total_stock} in stock)</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="text-sm text-muted-foreground mb-1 block">Starting Count *</label>
                  <Input
                    type="number"
                    min="0"
                    value={eventFormData.starting_count}
                    onChange={(e) => setEventFormData({...eventFormData, starting_count: e.target.value})}
                    placeholder="How many are you bringing?"
                    className="bg-secondary/50 border-transparent focus:border-primary"
                    required
                  />
                </div>

                <div>
                  <label className="text-sm text-muted-foreground mb-1 block">Notes</label>
                  <Input
                    value={eventFormData.notes}
                    onChange={(e) => setEventFormData({...eventFormData, notes: e.target.value})}
                    placeholder="Optional notes"
                    className="bg-secondary/50 border-transparent focus:border-primary"
                  />
                </div>

                <Button type="submit" className="w-full">
                  <Save className="h-4 w-4 mr-2" /> Add to Event
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
